/**
 * @module features/devices/components/DeviceCredentialsCard
 * @description Super Admin card showing a device's `device_secret` and
 * `device_signature_key`: masked by default, reveal toggle (server-audited fetch),
 * safe clipboard copy, and a Rotate Secrets confirmation dialog. Handles all four
 * UI states (loading / error with request id / empty / success). Plaintext values
 * live only in component state and are never written to storage or the query cache.
 */
import * as React from "react";
import { AlertCircle, Check, Copy, Eye, EyeOff, KeyRound, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AppError } from "@/lib/api-client";
import {
  useDeviceCredentials,
  useRevealDeviceCredentials,
  useRotateDeviceCredentials,
} from "../api/credentials-api";
import { formatDateTimeSeconds, formatUtc } from "../utils/format";

interface DeviceCredentialsCardProps {
  deviceId: string;
  deviceName: string;
  /** Decommissioned devices cannot be rotated. */
  disabled?: boolean;
}

interface Plain {
  device_secret: string;
  device_signature_key: string;
}

function SecretRow({
  label,
  value,
  revealed,
  testId,
}: {
  label: string;
  value: string;
  revealed: boolean;
  testId: string;
}) {
  const [copied, setCopied] = React.useState(false);
  const [copyFailed, setCopyFailed] = React.useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setCopyFailed(false);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be blocked (insecure context / permissions): tell the user.
      setCopyFailed(true);
    }
  };

  return (
    <div className="space-y-1">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        <code
          data-testid={testId}
          className="flex-1 min-w-0 rounded-lg border border-border/80 bg-muted/40 px-3 py-2 font-mono text-[11px] text-foreground break-all select-all"
        >
          {value}
        </code>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={copy}
          disabled={!revealed}
          aria-label={`Copy ${label}`}
          title={revealed ? "Copy to clipboard" : "Reveal to copy"}
          className="h-9 w-9 shrink-0"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      {copyFailed && (
        <p role="alert" className="text-[11px] text-destructive">
          Copy was blocked by the browser. Select the value and copy it manually.
        </p>
      )}
    </div>
  );
}

export function DeviceCredentialsCard({ deviceId, deviceName, disabled }: DeviceCredentialsCardProps) {
  const { data, isLoading, isError, error, refetch } = useDeviceCredentials(deviceId);
  const revealMutation = useRevealDeviceCredentials();
  const rotateMutation = useRotateDeviceCredentials();

  const [plain, setPlain] = React.useState<Plain | null>(null);
  const [actionError, setActionError] = React.useState<AppError | null>(null);
  const [rotateOpen, setRotateOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");

  // Drop plaintext whenever the inspected device changes or the card unmounts.
  React.useEffect(() => {
    setPlain(null);
    setActionError(null);
    return () => setPlain(null);
  }, [deviceId]);

  const toggleReveal = async () => {
    setActionError(null);
    if (plain) {
      setPlain(null);
      return;
    }
    try {
      const res = await revealMutation.mutateAsync(deviceId);
      setPlain({ device_secret: res.device_secret, device_signature_key: res.device_signature_key });
    } catch (err) {
      setActionError(AppError.fromUnknown(err, "Failed to reveal credentials."));
    }
  };

  const confirmRotate = async () => {
    setActionError(null);
    try {
      const res = await rotateMutation.mutateAsync({ deviceId, reason: reason.trim() });
      setPlain({ device_secret: res.device_secret, device_signature_key: res.device_signature_key });
      setRotateOpen(false);
      setReason("");
    } catch (err) {
      setActionError(AppError.fromUnknown(err, "Failed to rotate credentials."));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" data-testid="credentials-skeleton">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-3 w-56" />
      </div>
    );
  }

  if (isError || !data) {
    const appErr = error as AppError | null;
    const notFound = appErr?.errorCode === "CREDENTIALS_NOT_FOUND";
    if (notFound) {
      return (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-5 text-center">
          <KeyRound className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden="true" />
          <p className="mt-2 text-sm font-semibold">No credentials yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Rotate secrets to generate credentials for this device.
          </p>
        </div>
      );
    }
    return (
      <div role="alert" className="rounded-xl border border-danger/20 bg-danger-soft p-5 text-center text-danger">
        <AlertCircle className="mx-auto h-7 w-7" aria-hidden="true" />
        <p className="mt-2 text-sm font-semibold">Failed to load credentials</p>
        <p className="mt-1 text-xs">{appErr?.errorDetails ?? "Something went wrong."}</p>
        {appErr?.requestId && (
          <p className="mt-2 font-mono text-[11px] text-muted-foreground break-all">
            Request ID: {appErr.requestId}
          </p>
        )}
        <Button variant="outline" size="sm" className="mt-3 bg-card text-foreground" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const revealed = plain !== null;
  const secretValue = plain?.device_secret ?? data.device_secret;
  const keyValue = plain?.device_signature_key ?? data.device_signature_key;

  return (
    <div className="space-y-4">
      {actionError && (
        <div role="alert" className="flex items-start gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="break-words min-w-0">
            {actionError.errorDetails}
            {actionError.requestId ? ` (Request ID: ${actionError.requestId})` : ""}
          </span>
        </div>
      )}

      <SecretRow label="Device secret (X-Device-Secret)" value={secretValue} revealed={revealed} testId="device-secret" />
      <SecretRow label="Signature key (HMAC-SHA256)" value={keyValue} revealed={revealed} testId="device-signature-key" />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground" title={formatUtc(data.secret_rotated_at)}>
          Last rotated {formatDateTimeSeconds(data.secret_rotated_at)} · {data.rotation_count} rotation
          {data.rotation_count === 1 ? "" : "s"}
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleReveal}
            disabled={revealMutation.isPending}
            className="h-8 gap-1.5 text-xs"
          >
            {revealMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : revealed ? (
              <EyeOff className="h-3.5 w-3.5" />
            ) : (
              <Eye className="h-3.5 w-3.5" />
            )}
            {revealed ? "Hide" : "Reveal"}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setRotateOpen(true)}
            disabled={disabled}
            className="h-8 gap-1.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Rotate Secrets
          </Button>
        </div>
      </div>

      {data.rotation_history.length > 0 && (
        <details className="rounded-lg border border-border/80 bg-card px-3 py-2">
          <summary className="cursor-pointer text-xs font-medium">Rotation history</summary>
          <ul className="mt-2 space-y-1.5">
            {data.rotation_history.map((r) => (
              <li key={r.id} className="text-[11px] text-muted-foreground">
                <span className="text-foreground" title={formatUtc(r.rotated_at)}>
                  {formatDateTimeSeconds(r.rotated_at)}
                </span>
                {r.rotated_by_name ? ` · ${r.rotated_by_name}` : ""} ·{" "}
                <span className="font-mono">{r.new_secret_prefix}</span>
                {r.reason ? ` · ${r.reason}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}

      <Dialog open={rotateOpen} onOpenChange={(o) => !o && !rotateMutation.isPending && setRotateOpen(false)}>
        <DialogContent className="sm:max-w-[420px]" sheetOnMobile>
          <DialogHeader>
            <DialogTitle className="text-base">Rotate secrets for {deviceName}?</DialogTitle>
            <DialogDescription className="text-xs">
              New credentials are issued immediately. The device will be rejected until it is
              reconfigured with the new secret and signature key.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <label htmlFor="rotate_reason" className="text-xs font-medium block">
              Reason (optional)
            </label>
            <Input
              id="rotate_reason"
              value={reason}
              maxLength={255}
              onChange={(e) => setReason(e.target.value)}
              className="h-10 text-sm bg-card"
            />
          </div>
          <DialogFooter className="gap-2 sm:justify-between pt-2">
            <Button type="button" variant="outline" onClick={() => setRotateOpen(false)} disabled={rotateMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmRotate}
              disabled={rotateMutation.isPending}
              className="min-w-[120px]"
            >
              {rotateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rotating...
                </>
              ) : (
                "Rotate"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

